import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

// Components

import AddressesInfo from './summaryIncludes/AddressesInfo';
import CitizenshipImmigrationStatusInfo from './summaryIncludes/CitizenshipImmigrationStatusInfo';
import GenderIdentificationSexualOrientation from './summaryIncludes/GenderIdentificationSexualOrientation';
import FinancialFlowSecondSectionInfo from './summaryIncludes/FinancialFlowSecondSectionInfo';
import MilitaryInfo from './summaryIncludes/MilitaryInfo';
import RaceAndEthnicityInfo from './summaryIncludes/RaceAndEthnicityInfo';
import SsnInfo from './summaryIncludes/SsnInfo';
import SubHeader from '../../../headers/SubHeader';
import SummaryItem from './SummaryItem';
import SummaryFilter from '../summary/SummaryFilter';
import TribeInfo from './summaryIncludes/TribeInfo';

// Selectors
import {
    getSsapData,
    selectCurrentlyPregnantAndEnrolled,
    selectIfAdditionalResidencyEnabled,
    selectIfEnableAddressConfidentiality,
    selectMedicaidEndDateOverride
} from '@reducers/appReducer';

// Utils

import { getMarriageStatus } from '@utils/householdMember/getMarriageStatus';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Constants

import { GENDER_TYPES, NO_PRIOR_DATA } from '@constants/ssap/miscellaneous/miscellaneous';
import { PAGES } from '@constants/ssap/flows/pagesTypes';
import { STATE_NAMES } from '@constants/configs/states';

// Types

import { SsapSummaryType } from '@components/ssap/SsapTypes';


const FamilyAndHouseholdSummary_NV = props => {

    const {
        addressVisibilityDueToGeneralDelivery,
        applyingForFinancialAssistanceIndicator,
        householdMembers,
        buttonContainer: ButtonContainer,
        exchangeAndProgramNames,
        hiddenSummaryItems,
        indexMapper,
        isSogiFeatureEnabled,
        isState,
        medicaidTerminationFeatureEnabled,
        relations,
        taxHousehold,
        taxHousehold: {
            bloodRelationship,
        },
        flowType,
        serverDate,
        stateCode,
        tribes
    } = props;

    const intlApi = useIntl();
    const { addressConfidentialityParticipant } = useSelector(getSsapData);
    const additionalResidencyEnabled = useSelector(selectIfAdditionalResidencyEnabled);
    const medicaidEndDateOverride = useSelector(selectMedicaidEndDateOverride);
    const showAddressConfidentiality = useSelector(selectIfEnableAddressConfidentiality);
    const currentlyPregnantAndEnrolled = useSelector(selectCurrentlyPregnantAndEnrolled) ?? [];

    return householdMembers.map((member, idx) => {
        const {
            applicantGuid,
            applyingForCoverageIndicator,
            citizenshipImmigrationStatus,
            ethnicityAndRace,
            gender,
            genderIdentification,
            householdContact: {
                homeAddress,
                mailingAddress,
                residencyInfo
            },
            name,
            otherAddress: {
                livingOutsideofStateTemporarilyIndicator
            },
            personId,
            returnedFromTourOfActiveDuty,
            sexualOrientation,
            socialSecurityCard
        } = member;

        const getIsMarried = (personId, bloodRelationship) => {
            return getMarriageStatus(personId, bloodRelationship);
        } ;

        const summaryId = SsapSummaryType.FAMILY_AND_HOUSEHOLD.processReplacements({ idx });

        const getGender = gender => {
            return gender
                ? intlApi.formatMessage(
                    {
                        id: gender.toLowerCase() === GENDER_TYPES.FEMALE
                            ? 'common.female'
                            : 'common.male'
                    }
                )
                : NO_PRIOR_DATA;
        };

        const personName = normalizeNameOfPerson(name);

        return (
            <SummaryFilter
                key = { applicantGuid }
                indexMapper = { indexMapper }
                summaryId = { summaryId }
            >
                <div
                    className={`summary__block margin-b-20 ${idx === 0 ? '' : 'break_before'}`} >
                    <div className="summary__header">
                        <SubHeader
                            dataTestId={`Household-Info-Header-${applicantGuid}`}
                            text='ssap.subheader.non_internationalized_text'
                            textReplacements={{
                                text: (
                                    <FormattedMessage
                                        id = 'ssap.subheader.person_name_primary_contact'
                                        values = { {
                                            personName,
                                            primaryContact: (
                                                <FormattedMessage
                                                    id = {
                                                        idx === 0
                                                            ? 'common.primary_contact'
                                                            : 'common.no_text'
                                                    }
                                                />
                                            )
                                        } }
                                    />
                                )
                            }}
                            headerClassName={'gi-heading-alt'}
                            additionalElement={(
                                <ButtonContainer
                                    type="editButton_secondary"
                                    args={[idx, PAGES.PERSONAL_INFORMATION_PAGE]}
                                    ariaLabel={
                                        intlApi.formatMessage(
                                            { id: 'ssap.edit.household_information' },
                                            { firstName: name.firstName, lastName: name.lastName },
                                        )
                                    }
                                />
                            )}
                            additionalElementClassName={'header-btn'}
                        />
                    </div>
                    <div className="grid-row">
                        <SummaryItem
                            textCode = 'common.applying_for_coverage'
                            value = { applyingForCoverageIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].applyingForCoverageIndicator`
                                    ]
                                ]
                            }
                        />
                    </div>

                    <AddressesInfo
                        { ... {
                            applicantGuid,
                            applyingForCoverageIndicator,
                            customAddressItemProps: ['addressItemClassesWithSidePaddings'],
                            addressVisibilityDueToGeneralDelivery,
                            addressConfidentialityParticipant,
                            showAddressConfidentiality: showAddressConfidentiality && idx === 0,
                            additionalResidencyEnabled,
                            residencyInfo,
                            homeAddress,
                            hiddenSummaryItems,
                            isState,
                            mailingAddress,
                            mailingAddressFromPortal: null,
                            pcpIndex: idx
                        } }
                    />

                    {
                        livingOutsideofStateTemporarilyIndicator !== null

                            ? (
                                <div className="grid-row">
                                    <SummaryItem
                                        replacements = { { exchangeState: STATE_NAMES[stateCode] } }
                                        textCode = 'common.are_you_living_outside_state'
                                        value = { livingOutsideofStateTemporarilyIndicator }
                                        noPriorDataValues = { [ null ] }
                                        desc = {
                                            [
                                                [
                                                    `taxHousehold.householdMember[${ idx }].otherAddress.livingOutsideofStateTemporarilyIndicator`
                                                ]
                                            ]
                                        }
                                    />
                                </div>
                            )
                            : null
                    }


                    {
                        gender &&
                            <SummaryItem
                                textCode = 'common.gender'
                                value = { getGender(gender) }
                                ignoreIntl
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${ idx }].gender`,
                                            getGender
                                        ]
                                    ]
                                }
                            />
                    }

                    <SsnInfo
                        { ...{
                            ...socialSecurityCard,
                            applyingForCoverageIndicator,
                            idx
                        } }
                    />

                    <CitizenshipImmigrationStatusInfo
                        { ...{
                            applyingForCoverageIndicator,
                            citizenshipImmigrationStatus,
                            personName,
                            isState,
                            idx
                        } }
                    />

                    <RaceAndEthnicityInfo
                        { ...{
                            ethnicityAndRace,
                            idx
                        } }
                    />

                    <GenderIdentificationSexualOrientation
                        { ...{
                            genderIdentification,
                            idx,
                            isSogiFeatureEnabled,
                            member,
                            personName,
                            serverDate,
                            sexualOrientation
                        } }
                    />

                    <SummaryItem
                        textCode = 'label.are_you_married'
                        value = { getIsMarried(personId, bloodRelationship) }
                        desc = {
                            [
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].personId`,
                                        'taxHousehold.bloodRelationship'
                                    ],
                                    getIsMarried
                                ]
                            ]
                        }
                    />

                    <MilitaryInfo
                        { ...{
                            applyingForFinancialAssistanceIndicator,
                            idx,
                            isState,
                            member,
                            personName,
                        } }
                    />

                    <TribeInfo
                        { ...{
                            idx,
                            member,
                            tribes
                        } }
                    />

                    <FinancialFlowSecondSectionInfo
                        { ...{
                            currentlyPregnantAndEnrolled,
                            exchangeAndProgramNames,
                            flowType,
                            idx,
                            isState,
                            medicaidEndDateOverride,
                            medicaidTerminationFeatureEnabled,
                            member,
                            relations,
                            serverDate,
                            stateCode,
                            taxHousehold
                        } }
                    />

                </div>
            </SummaryFilter>
        );
    });
};

export default FamilyAndHouseholdSummary_NV;
